import { Heart, MessageCircle, Trophy } from "lucide-react";
import { FEATURE_ICONS, FEATURE_VARIANTS } from "@/mock";
import { useLanguage } from "@/i18n/LanguageContext";

const FEED_POSTS = [
  { initial: "M", name: "Maria", sport: "Tennis", text: "Match point! 🎾 Great session today.", likes: 128, comments: 14 },
  { initial: "J", name: "João", sport: "Football", text: "Looking for 2 more players this Sunday.", likes: 86, comments: 23 },
  { initial: "A", name: "Ana", sport: "Running", text: "New personal best on the 10K!", likes: 204, comments: 9 },
];

const variantStyles = {
  dark: "bg-[#1A1A1A] text-white",
  orange: "bg-[#F2641E] text-white",
  light: "bg-white text-[#111] border border-neutral-100",
};

const Features = () => {
  const { t } = useLanguage();
  return (
    <section id="features" data-testid="features-section" className="bg-[#F4F4F2] pt-4 pb-8 md:pt-5 md:pb-10">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-8">
        <div className="reveal mb-10">
          <div className="text-[#2E4FE6] font-bold text-xs tracking-[0.22em] uppercase mb-5 mx-auto">{t.features.eyebrow}</div>
          <h2 className="font-display text-[clamp(1.9rem,4.5vw,3.8rem)] uppercase text-[#111] break-words max-w-4xl mx-auto">{t.features.title}</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {t.features.items.map((f, i) => {
            const Icon = FEATURE_ICONS[i];
            const variant = FEATURE_VARIANTS[i];
            const isFirst = i === 0;
            return (
              <div
                key={i}
                data-testid={`feature-card-${i}`}
                className={`reveal group relative overflow-hidden rounded-lg p-8 md:p-10 min-h-[280px] flex flex-col justify-between transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${variantStyles[variant]} ${
                  isFirst ? "md:row-span-2 md:min-h-[580px]" : ""
                }`}
              >
                <div>
                  <div className={`text-xs font-bold tracking-[0.18em] uppercase mb-6 ${variant === "light" ? "text-[#2E4FE6]" : "text-white/70"}`}>{f.index}</div>
                  <h3 className="font-display text-[clamp(1.8rem,3vw,2.8rem)] uppercase mb-4">{f.title}</h3>
                  <p className={`text-base leading-relaxed max-w-md mx-auto ${variant === "light" ? "text-neutral-600" : "text-white/85"}`}>{f.text}</p>
                </div>
                {isFirst && (
                  <div className="flex flex-col gap-3 flex-1 justify-center py-6 max-w-sm mx-auto w-full" aria-hidden="true">
                    {FEED_POSTS.map((p, r) => (
                      <div
                        key={r}
                        className="feed-row rounded-lg bg-white/[0.05] border border-white/10 px-4 py-3.5 backdrop-blur-sm"
                        style={{ animationDelay: `${r * 0.8}s` }}
                      >
                        <div className="flex items-center gap-2.5 mb-2">
                          <div className="w-8 h-8 rounded-full bg-[#F2641E] flex items-center justify-center text-white text-sm font-bold shrink-0">{p.initial}</div>
                          <div className="min-w-0 text-left">
                            <div className="text-white text-sm font-semibold leading-none">{p.name}</div>
                            <div className="flex items-center gap-1 text-white/50 text-[11px] mt-1">
                              <Trophy className="w-3 h-3 text-[#F2641E]" />
                              {p.sport}
                            </div>
                          </div>
                        </div>
                        <p className="text-white/80 text-[13px] leading-snug text-left mb-2.5">{p.text}</p>
                        <div className="flex items-center gap-4 text-white/50 text-[11px]">
                          <span className="flex items-center gap-1">
                            <Heart className="w-3.5 h-3.5 text-[#F2641E]" />
                            {p.likes}
                          </span>
                          <span className="flex items-center gap-1">
                            <MessageCircle className="w-3.5 h-3.5" />
                            {p.comments}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex justify-center mt-8">
                  <Icon className={`w-12 h-12 group-hover:scale-110 transition-transform duration-300 ${variant === "light" ? "text-[#111]" : "text-white"}`} strokeWidth={1.4} />
                </div>
                {isFirst && <div className="absolute -right-10 top-1/2 -translate-y-1/2 w-40 h-40 border-2 border-[#F2641E]/40 rotate-12 rounded-sm pointer-events-none" />}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Features;
